import * as Clipboard from 'expo-clipboard';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from 'react-native-toast-notifications';

import { Box, ShadowBox } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import Text from '@components/Text/Text';
import {
  GetMeDocument,
  useGeneratePublicCalendarUrlMutation,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useCloseOnOutsideClick } from '@hooks/useOutsideClick.web';
import CustomToast from '@screens/Tasks/CustomToast';

export const PublicCalendarLinkButtonV3: React.FC = () => {
  const { me, refetchMe } = useMe();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toast = useToast();
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [generateCalendarUrl, { error: generateUrlError }] =
    useGeneratePublicCalendarUrlMutation({
      refetchQueries: [{ query: GetMeDocument }, 'getMe'],
    });

  useEffect(() => {
    if (!generateUrlError) return;
    toast.show(<CustomToast message='Failed to generate calendar link' />, {
      type: 'custom',
      placement: 'bottom',
      duration: 2000,
      animationType: 'zoom-in',
    });
  }, [generateUrlError, toast]);

  const buttonRef = useCloseOnOutsideClick<HTMLDivElement>(isModalOpen, () =>
    setIsModalOpen(false)
  );

  const handleOnPress = () => {
    if (!me?.calendarUrl) {
      generateCalendarUrl()
        .then(() => refetchMe())
        .catch(() => {});
    }
    setIsModalOpen(true);
    buttonRef.current &&
      setModalPosition({
        top: buttonRef.current.getBoundingClientRect().bottom,
        left: buttonRef.current.getBoundingClientRect().left,
        width: buttonRef.current.getBoundingClientRect().width,
      });
  };

  const customToast = (message: string) => {
    toast.show(<CustomToast message={message} />, {
      type: 'custom',
      placement: 'bottom',
      duration: 2000,
      animationType: 'zoom-in',
    });
  };
  return (
    <Box position='relative'>
      {/* marginLeft lives on this wrapper, not the Button, so buttonRef's
          measured rect (used to position the popover below) lines up with
          the button's actual visible edge instead of including the margin
          as empty space inside the ref'd box (TA-10215). */}
      <Box ref={buttonRef} marginLeft='xs'>
        {/* Hover handled by Button itself (TA-9005): white rests, grey01 on
            hover. Hand-rolling it via the backgroundColor prop feeds grey01
            back in as the resting color and escalates the hover to grey02. */}
        <Button
          height={36}
          borderWidth={0}
          prefix={<Icon variant='s' name='Calendar' color='black' />}
          onPress={handleOnPress}
          backgroundColor='white'
          isSmall={true}
          borderRadius='xs'
          variant='create'
          accessibilityLabel='Add to Calendar'>
          <Text variant='labelSmall' color='black'>
            Add to Calendar
          </Text>
        </Button>
      </Box>

      {isModalOpen &&
        createPortal(
          <Box
            position='absolute'
            style={{
              top: modalPosition.top,
              left: modalPosition.left,
              width: modalPosition.width,
            }}>
            <Box flexShrink={1}>
              <ShadowBox
                flex={1}
                flexDirection='row'
                borderRadius='xs'
                backgroundColor='white'
                justifyContent='flex-start'>
                <Button
                  onPress={() => {
                    Clipboard.setStringAsync(me!.calendarUrl!)
                      .then(() => {
                        customToast('Calendar Link copied to clipboard');
                      })
                      .then(() => {
                        setIsModalOpen(false);
                      });
                  }}
                  variant='text'
                  fullWidth
                  alignTextLeft
                  prefix={<Icon variant='s' name='Link' color='black' />}>
                  <Text variant='webSmall'>Copy URL</Text>
                </Button>
              </ShadowBox>
            </Box>
          </Box>,
          document.body
        )}
    </Box>
  );
};
